// Ported from Kratos UncaughtExceptionListener: https://github.zappos.amazon.dev/mweb/kratos/pull/2640

import ExecutionEnvironment from 'exenv';

import { getType } from 'history/historyFactory';
import { trackUncaughtException } from 'events/ops/opsEvent';
import { trackRumEvent } from 'helpers/rum';
import { buildDeduplicationKey, buildUncaughtExceptionPayload, shouldEmitError, truncateMessage } from 'utils/uncaughtExceptionUtils';

const MAX_MESSAGE_LENGTH = 256;
const emittedKeys = new Set<string>();

function extractRejectionMessage(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.message;
  }
  if (typeof reason === 'string') {
    return reason;
  }
  return String(reason);
}

export default function initUncaughtExceptionListener(siteDomain: string): void {
  if (!ExecutionEnvironment.canUseDOM) {
    return;
  }

  window.addEventListener('error', (event: ErrorEvent) => {
    try {
      const { message, filename, lineno, colno } = event;

      if (!shouldEmitError(message, filename, siteDomain)) {
        return;
      }

      const key = buildDeduplicationKey(message, filename, lineno, colno);
      if (emittedKeys.has(key)) {
        return;
      }
      emittedKeys.add(key);

      const truncatedMessage = truncateMessage(message, MAX_MESSAGE_LENGTH);
      const pageType = getType(window.location.pathname) || 'unknown';
      const payload = buildUncaughtExceptionPayload(truncatedMessage, filename, lineno, colno, pageType, 'error');

      trackRumEvent('uncaught_exception', payload);
      trackUncaughtException(pageType);
    } catch {
      // Silently swallow to prevent recursive error loops
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    try {
      const message = extractRejectionMessage(event.reason);

      const key = buildDeduplicationKey(message, '', 0, 0);
      if (emittedKeys.has(key)) {
        return;
      }
      emittedKeys.add(key);

      const truncatedMessage = truncateMessage(message, MAX_MESSAGE_LENGTH);
      const pageType = getType(window.location.pathname) || 'unknown';
      const payload = buildUncaughtExceptionPayload(truncatedMessage, '', 0, 0, pageType, 'unhandledrejection');

      trackRumEvent('uncaught_exception', payload);
      trackUncaughtException(pageType);
    } catch {
      // Silently swallow to prevent recursive error loops
    }
  });
}
